import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { maintenanceAPI } from '../utils/api';
import { MaintenanceSchedule, MaintenanceRecord, MaintenanceTemplateTask } from '../types';
import { Save, X, ArrowLeft, CheckCircle, XCircle, DollarSign, User } from 'lucide-react';

const MaintenanceRecordForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const scheduleId = searchParams.get('schedule_id');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState<MaintenanceSchedule | null>(null);
  const [record, setRecord] = useState<Partial<MaintenanceRecord>>({
    maintenance_schedule_id: scheduleId ? parseInt(scheduleId) : undefined,
    service_date: new Date().toISOString().split('T')[0],
    service_run_hours: undefined,
    service_notes: '',
    cost: undefined,
    service_provider: '',
    completed_tasks: [],
    skipped_tasks: [],
    service_status: 'completed',
  });

  useEffect(() => {
    if (scheduleId) {
      loadSchedule(parseInt(scheduleId));
    }
    if (isEdit && id) {
      loadRecord(parseInt(id));
    }
  }, [scheduleId, id, isEdit]);

  const loadSchedule = async (scheduleId: number) => {
    try {
      setLoading(true);
      const response = await maintenanceAPI.getSchedule(scheduleId);
      setSchedule(response.schedule);
      setRecord(prev => ({
        ...prev,
        maintenance_schedule_id: scheduleId,
        service_run_hours: response.schedule.current_run_hours || undefined,
      }));
    } catch (error: any) {
      console.error('Error loading schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecord = async (recordId: number) => {
    try {
      setLoading(true);
      const response = await maintenanceAPI.getRecord(recordId);
      setRecord(response.record);
      if (response.record.schedule) {
        setSchedule(response.record.schedule);
      }
    } catch (error: any) {
      console.error('Error loading record:', error);
      alert('Failed to load record');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!record.maintenance_schedule_id) {
      alert('Schedule is required');
      return;
    }

    try {
      setSaving(true);
      
      if (isEdit && id) {
        await maintenanceAPI.updateRecord(parseInt(id), record);
      } else {
        await maintenanceAPI.createRecord(record);
      }
      
      navigate('/maintenance');
    } catch (error: any) {
      console.error('Error saving record:', error);
      alert(error.response?.data?.error || 'Failed to save record');
    } finally {
      setSaving(false);
    }
  };

  const toggleTask = (taskId: number, isCompleted: boolean) => {
    const completed = [...(record.completed_tasks || [])];
    const skipped = [...(record.skipped_tasks || [])];

    if (isCompleted) {
      if (!completed.includes(taskId)) {
        completed.push(taskId);
      }
      const skippedIndex = skipped.indexOf(taskId);
      if (skippedIndex > -1) {
        skipped.splice(skippedIndex, 1);
      }
    } else {
      const completedIndex = completed.indexOf(taskId);
      if (completedIndex > -1) {
        completed.splice(completedIndex, 1);
      }
    }

    setRecord({ ...record, completed_tasks: completed, skipped_tasks: skipped });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const tasks = schedule?.template?.tasks || [];
  const completedTasks = record.completed_tasks || [];
  const allTasksCompleted = tasks.length > 0 && tasks.every(t => t.id && completedTasks.includes(t.id));

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/maintenance')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {isEdit ? 'Edit Record' : 'Record Service'}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {schedule?.generator?.name && (
                    <>
                      Generator: {schedule.generator.name}
                      {schedule.generator.device_name && (
                        <span className="ml-1">({schedule.generator.device_name})</span>
                      )}
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/maintenance')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Service Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Service Details</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Date *
                  </label>
                  <input
                    type="date"
                    value={record.service_date || ''}
                    onChange={(e) => setRecord({ ...record, service_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Run Hours
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={record.service_run_hours || ''}
                    onChange={(e) => setRecord({ ...record, service_run_hours: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Current run hours"
                    min="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Status
                </label>
                <select
                  value={record.service_status || 'completed'}
                  onChange={(e) => setRecord({ ...record, service_status: e.target.value as any })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="completed">Completed</option>
                  <option value="partial">Partial</option>
                  <option value="skipped">Skipped</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Notes
                </label>
                <textarea
                  value={record.service_notes || ''}
                  onChange={(e) => setRecord({ ...record, service_notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Notes about the service performed..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Cost
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={record.cost || ''}
                    onChange={(e) => setRecord({ ...record, cost: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Service Provider
                  </label>
                  <input
                    type="text"
                    value={record.service_provider || ''}
                    onChange={(e) => setRecord({ ...record, service_provider: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Who performed the service?"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Checklist */}
          {tasks.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Checklist</h2>
                {allTasksCompleted && (
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    All tasks completed
                  </span>
                )}
              </div>
              <div className="space-y-3">
                {tasks.map((task) => {
                  const isCompleted = task.id ? completedTasks.includes(task.id) : false;
                  return (
                    <motion.div
                      key={task.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        isCompleted ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                      }`}
                      onClick={() => task.id && toggleTask(task.id, !isCompleted)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-1 ${isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                          {isCompleted ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className={`font-medium ${isCompleted ? 'text-green-900 line-through' : 'text-gray-900'}`}>
                              {task.task_name}
                            </h3>
                            {task.is_required && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                                Required
                              </span>
                            )}
                          </div>
                          {task.task_description && (
                            <p className="text-sm text-gray-600 mt-1">{task.task_description}</p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceRecordForm;

